import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { cacheData, getCachedData } from "@/utils/offlineQueue";
import {
  AlertTriangle, Users, Activity, MessageSquare, Plus, TrendingUp,
  Clock, CheckCircle2, UserCheck, Stethoscope, ShieldAlert, ChevronRight,
  CalendarX, Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RISK_LABELS = { critico: "Crítico", alto: "Alto", medio: "Médio", baixo: "Baixo" };
const WOUND_LABELS = { ulcera_venosa: "Úlcera Venosa", pe_diabetico: "Pé Diabético", lesao_por_pressao: "Lesão por Pressão" };

const ROLE_LABELS = {
  secretario: "Secretário(a)", coordenador: "Coordenador(a) APS",
  enfermeiro: "Enfermeiro(a)", medico: "Médico(a)", tecnico: "Técnico(a) Enf.",
  acs: "ACS", especialista: "Especialista", admin: "Administrador"
};

const URGENCY_CONFIG = {
  critico: {
    bg: "bg-red-50", border: "border-red-300",
    badge: "bg-red-100 text-red-800", iconColor: "text-red-500",
    label: "21+ dias — Crítico"
  },
  alto: {
    bg: "bg-orange-50", border: "border-orange-300",
    badge: "bg-orange-100 text-orange-800", iconColor: "text-orange-500",
    label: "14+ dias — Alto"
  },
  medio: {
    bg: "bg-yellow-50", border: "border-yellow-200",
    badge: "bg-yellow-100 text-yellow-800", iconColor: "text-yellow-600",
    label: "7+ dias — Atenção"
  },
};

/* ── CARDS: Feridas Estagnadas ── */
function StagnantWoundsCards({ wounds, navigate }) {
  if (!wounds || wounds.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-border p-5 animate-fade-in" data-testid="stagnant-wounds-section">
      <div className="flex items-center gap-2 mb-4">
        <CalendarX className="w-5 h-5 text-orange-500" />
        <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>
          Feridas sem Evolução
        </h2>
        <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
          {wounds.length} ferida{wounds.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {wounds.slice(0, 6).map(w => {
          const cfg = URGENCY_CONFIG[w.urgency] || URGENCY_CONFIG.medio;
          return (
            <div
              key={w.wound_id}
              className={`${cfg.bg} ${cfg.border} border rounded-xl p-4 cursor-pointer hover:opacity-80 transition-all`}
              onClick={() => navigate(`/wounds/${w.wound_id}`)}
              data-testid={`stagnant-wound-${w.wound_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-semibold text-foreground leading-tight truncate flex-1">{w.patient_name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${cfg.badge}`}>
                  {w.days_without_evolution}d
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{w.wound_type_label}</p>
              {w.location && <p className="text-xs text-muted-foreground truncate">{w.location}</p>}
              <div className="mt-2 flex items-center gap-1">
                <Clock className={`w-3 h-3 flex-shrink-0 ${cfg.iconColor}`} />
                <span className={`text-xs font-medium ${cfg.iconColor}`}>{cfg.label}</span>
              </div>
            </div>
          );
        })}
      </div>
      {wounds.length > 6 && (
        <p className="text-xs text-muted-foreground text-center mt-3">
          +{wounds.length - 6} outra{wounds.length - 6 !== 1 ? "s" : ""} ferida{wounds.length - 6 !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-border p-5 hover-lift animate-fade-in">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>{value}</p>
      <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function PatientRow({ patient, navigate }) {
  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-secondary/50 transition-all"
      onClick={() => navigate(`/patients/${patient.patient_id}`)}
      data-testid={`patient-card-${patient.patient_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
          {(patient.name || "P")[0].toUpperCase()}
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{patient.social_name || patient.name}</p>
          <p className="text-xs text-muted-foreground">{patient.active_wounds_count || 0} lesão(ões) ativa(s)</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {patient.max_risk && patient.max_risk !== "baixo" && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full risk-${patient.max_risk}`}>
            {RISK_LABELS[patient.max_risk]}
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
    </div>
  );
}

/* ── GESTOR (admin/secretario/coordenador) ── */
function ManagerDashboard({ user }) {
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const [stats, setStats] = useState(() => getCachedData("dashboard_stats"));
  const [pendingCount, setPendingCount] = useState(0);
  const [stagnantWounds, setStagnantWounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOnline && stats) { setLoading(false); return; }
    Promise.all([
      axios.get(`${API}/dashboard/municipal`, { withCredentials: true }),
      axios.get(`${API}/admin/pending-validations`, { withCredentials: true }),
      axios.get(`${API}/alerts/stagnant-wounds`, { withCredentials: true }),
    ]).then(([dash, pending, stagnant]) => {
      setStats(dash.data);
      cacheData("dashboard_stats", dash.data);
      setPendingCount((pending.data || []).length);
      setStagnantWounds(stagnant.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isOnline]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
            Olá, {(user?.full_name || user?.name || "").split(" ")[0]}
          </h1>
          <p className="text-sm text-muted-foreground">{ROLE_LABELS[user?.role]} · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <button onClick={() => navigate("/patients/new")} className="hidden md:flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-all" data-testid="new-patient-btn-dashboard">
          <Plus className="w-4 h-4" />Novo Paciente
        </button>
      </div>

      {pendingCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center justify-between animate-fade-in" data-testid="pending-validation-alert">
          <div className="flex items-center gap-3">
            <UserCheck className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">{pendingCount} profissional(is) aguardando validação</p>
              <p className="text-xs text-yellow-700">Acesse Administração para aprovar</p>
            </div>
          </div>
          <button onClick={() => navigate("/admin")} className="text-xs font-medium text-yellow-800 hover:underline">Ver agora</button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl bg-white border border-border animate-pulse" />)}</div>
      ) : stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in">
          <StatCard label="Pacientes" value={stats.total_patients} icon={Users} color="bg-primary" testId="stat-patients" />
          <StatCard label="Lesões Ativas" value={stats.total_active_wounds} icon={Activity} color="bg-accent" testId="stat-wounds" />
          <StatCard label="Interconsultas Pend." value={stats.pending_teleconsultations} icon={MessageSquare} color="bg-yellow-500" testId="stat-telecons" />
          <StatCard label="Evoluções/Mês" value={stats.evolutions_this_month} icon={TrendingUp} color="bg-blue-500" testId="stat-evolutions" />
        </div>
      )}

      <StagnantWoundsCards wounds={stagnantWounds} navigate={navigate} />

      {/* Widget: Materiais do mês */}
      {stats?.total_items_month > 0 && (
        <div className="bg-white rounded-xl border border-border p-5 animate-fade-in" data-testid="materials-summary-widget">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Package className="w-4 h-4 text-emerald-700" />
              </div>
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>
                Materiais este Mês
              </h2>
            </div>
            <button
              onClick={() => navigate("/municipal")}
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="link-ver-previsao"
            >
              Ver previsão <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: "Manrope" }}>
            {stats.total_items_month}
            <span className="text-sm font-normal text-muted-foreground ml-1">itens utilizados</span>
          </p>
          <div className="space-y-1.5">
            {(stats.top_materials_month || []).map((mat, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-border/40 last:border-0">
                <span className="text-xs text-foreground truncate max-w-[200px]">{mat.material_name}</span>
                <span className="text-xs font-semibold text-primary flex-shrink-0">{mat.total} {mat.unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats?.recent_alerts?.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>Alertas Ativos</h2>
          </div>
          <div className="space-y-2">
            {stats.recent_alerts.map(alert => (
              <div key={alert.wound_id} className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-secondary/50 transition-all" onClick={() => navigate(`/wounds/${alert.wound_id}`)} data-testid={`alert-${alert.wound_id}`}>
                <div>
                  <p className="text-sm font-medium text-foreground">{alert.patient_name}</p>
                  <p className="text-xs text-muted-foreground">{WOUND_LABELS[alert.wound_type]}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full risk-${alert.risk_level}`}>{RISK_LABELS[alert.risk_level]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="md:hidden">
        <button onClick={() => navigate("/patients/new")} className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-xl text-sm font-medium" data-testid="new-patient-btn-mobile">
          <Plus className="w-4 h-4" />Novo Paciente
        </button>
      </div>
    </div>
  );
}

/* ── MÉDICO ── */
function DoctorDashboard({ user }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [telecons, setTelecons] = useState([]);
  const [stagnantWounds, setStagnantWounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/patients?limit=20`, { withCredentials: true }),
      axios.get(`${API}/teleconsultations?status=pendente`, { withCredentials: true }),
      axios.get(`${API}/alerts/stagnant-wounds`, { withCredentials: true }),
    ]).then(([p, t, stagnant]) => {
      setPatients(p.data.patients || []);
      setTelecons(t.data || []);
      setStagnantWounds(stagnant.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const criticalPatients = patients.filter(p => p.max_risk === "critico");
  const highPatients = patients.filter(p => p.max_risk === "alto");
  const myTelecons = telecons.filter(t => !t.specialist_id || t.specialist_id === user?.user_id);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
            Dr(a). {(user?.full_name || user?.name || "").split(" ")[0]}
          </h1>
          <p className="text-sm text-muted-foreground">Médico(a) · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 animate-fade-in">
        <StatCard label="Críticos" value={criticalPatients.length} icon={ShieldAlert} color="bg-destructive" testId="stat-critical" />
        <StatCard label="Alto Risco" value={highPatients.length} icon={AlertTriangle} color="bg-orange-500" testId="stat-high" />
        <StatCard label="Interconsultas" value={myTelecons.length} icon={MessageSquare} color="bg-blue-500" testId="stat-telecons-doctor" />
      </div>

      {criticalPatients.length > 0 && (
        <div className="bg-white rounded-xl border border-destructive/30 p-5 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-5 h-5 text-destructive" />
            <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>Pacientes Críticos</h2>
          </div>
          <div className="space-y-2">
            {criticalPatients.map(p => <PatientRow key={p.patient_id} patient={p} navigate={navigate} />)}
          </div>
        </div>
      )}

      <StagnantWoundsCards wounds={stagnantWounds} navigate={navigate} />

      <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>Meus Pacientes</h2>
          <button onClick={() => navigate("/patients")} className="text-sm text-primary font-medium hover:underline" data-testid="view-all-patients">Ver todos</button>
        </div>
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />)}</div>
        ) : patients.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Nenhum paciente vinculado</p>
        ) : (
          <div className="space-y-2">
            {patients.slice(0, 5).map(p => <PatientRow key={p.patient_id} patient={p} navigate={navigate} />)}
          </div>
        )}
      </div>

      {myTelecons.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-yellow-600" />
            <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>Interconsultas Pendentes</h2>
          </div>
          <div className="space-y-2">
            {myTelecons.slice(0, 3).map(c => (
              <div key={c.consult_id} className="flex items-center justify-between p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-secondary/50" onClick={() => navigate("/teleconsultation")} data-testid={`telecon-${c.consult_id}`}>
                <div>
                  <p className="text-sm font-medium">{c.patient_name || "Paciente"}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{c.question}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 ml-2">Pendente</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── ENFERMEIRO / TÉCNICO ── */
function NurseDashboard({ user }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [stagnantWounds, setStagnantWounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/patients?limit=30`, { withCredentials: true }),
      axios.get(`${API}/alerts/stagnant-wounds`, { withCredentials: true }),
    ]).then(([p, stagnant]) => {
      setPatients(p.data.patients || []);
      setStagnantWounds(stagnant.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const criticalHigh = patients.filter(p => ["critico", "alto"].includes(p.max_risk));
  const activeWoundsTotal = patients.reduce((sum, p) => sum + (p.active_wounds_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
            Enf. {(user?.full_name || user?.name || "").split(" ")[0]}
          </h1>
          <p className="text-sm text-muted-foreground">{ROLE_LABELS[user?.role]} · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <button onClick={() => navigate("/patients/new")} className="hidden md:flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-all" data-testid="new-patient-btn-dashboard">
          <Plus className="w-4 h-4" />Novo Paciente
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 animate-fade-in">
        <StatCard label="Pacientes" value={patients.length} icon={Users} color="bg-primary" testId="stat-patients" />
        <StatCard label="Lesões Ativas" value={activeWoundsTotal} icon={Activity} color="bg-accent" testId="stat-wounds" />
        <StatCard label="Atenção Urgente" value={criticalHigh.length} icon={AlertTriangle} color="bg-orange-500" testId="stat-urgent" />
      </div>

      {criticalHigh.length > 0 && (
        <div className="bg-white rounded-xl border border-orange-200 p-5 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <h2 className="text-base font-semibold" style={{ fontFamily: "Manrope" }}>Necessitam Atenção</h2>
          </div>
          <div className="space-y-2">
            {criticalHigh.map(p => <PatientRow key={p.patient_id} patient={p} navigate={navigate} />)}
          </div>
        </div>
      )}

      <StagnantWoundsCards wounds={stagnantWounds} navigate={navigate} />

      <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold" style={{ fontFamily: "Manrope" }}>Pacientes da Unidade</h2>
          <button onClick={() => navigate("/patients")} className="text-sm text-primary font-medium hover:underline">Ver todos</button>
        </div>
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />)}</div>
        ) : (
          <div className="space-y-2">
            {patients.slice(0, 5).map(p => <PatientRow key={p.patient_id} patient={p} navigate={navigate} />)}
          </div>
        )}
      </div>

      <div className="md:hidden">
        <button onClick={() => navigate("/patients/new")} className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-xl text-sm font-medium">
          <Plus className="w-4 h-4" />Novo Paciente
        </button>
      </div>
    </div>
  );
}

/* ── ACS ── */
function ACSDashboard({ user }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/patients?limit=50`, { withCredentials: true })
      .then(r => setPatients(r.data.patients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const myPatients = patients.filter(p => p.assigned_acs_id === user?.user_id);
  const urgentPatients = myPatients.filter(p => ["critico", "alto"].includes(p.max_risk));

  return (
    <div className="space-y-6">
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
          Olá, {(user?.full_name || user?.name || "").split(" ")[0]}
        </h1>
        <p className="text-sm text-muted-foreground">ACS · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 animate-fade-in">
        <StatCard label="Meus Pacientes" value={myPatients.length || patients.length} icon={Users} color="bg-primary" testId="stat-my-patients" />
        <StatCard label="Alertas" value={urgentPatients.length} icon={AlertTriangle} color="bg-orange-500" testId="stat-alerts-acs" />
      </div>

      {urgentPatients.length > 0 && (
        <div className="bg-white rounded-xl border border-orange-200 p-5 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <h2 className="text-base font-semibold" style={{ fontFamily: "Manrope" }}>Precisam de Visita</h2>
          </div>
          <div className="space-y-2">
            {urgentPatients.map(p => <PatientRow key={p.patient_id} patient={p} navigate={navigate} />)}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold" style={{ fontFamily: "Manrope" }}>Meus Pacientes</h2>
          <button onClick={() => navigate("/patients")} className="text-sm text-primary font-medium hover:underline">Ver todos</button>
        </div>
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />)}</div>
        ) : (
          <div className="space-y-2">
            {(myPatients.length > 0 ? myPatients : patients).slice(0, 6).map(p => (
              <PatientRow key={p.patient_id} patient={p} navigate={navigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── ESPECIALISTA ── */
function SpecialistDashboard({ user }) {
  const navigate = useNavigate();
  const [telecons, setTelecons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/teleconsultations`, { withCredentials: true })
      .then(r => setTelecons(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const pending = telecons.filter(t => t.status === "pendente");
  const answered = telecons.filter(t => t.status === "respondida");

  return (
    <div className="space-y-6">
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
          Dr(a). {(user?.full_name || user?.name || "").split(" ")[0]}
        </h1>
        <p className="text-sm text-muted-foreground">Especialista · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 animate-fade-in">
        <StatCard label="Aguardando resposta" value={pending.length} icon={Clock} color="bg-yellow-500" testId="stat-pending-telecons" />
        <StatCard label="Respondidas" value={answered.length} icon={CheckCircle2} color="bg-green-500" testId="stat-answered" />
      </div>

      <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold" style={{ fontFamily: "Manrope" }}>Interconsultas Pendentes</h2>
          <button onClick={() => navigate("/teleconsultation")} className="text-sm text-primary font-medium hover:underline">Ver todas</button>
        </div>
        {loading ? (
          <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />)}</div>
        ) : pending.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Todas as teleconsultas respondidas</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map(c => (
              <div key={c.consult_id} className="flex items-start justify-between p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-secondary/50" onClick={() => navigate("/teleconsultation")} data-testid={`telecon-${c.consult_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{c.patient_name || "Paciente"}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{c.question}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 ml-2 flex-shrink-0">Pendente</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── MAIN COMPONENT ── */
export default function DashboardPage() {
  const { user } = useAuth();

  const renderDashboard = () => {
    switch (user?.role) {
      case "admin":
      case "secretario":
      case "coordenador":
        return <ManagerDashboard user={user} />;
      case "medico":
        return <DoctorDashboard user={user} />;
      case "enfermeiro":
      case "tecnico":
        return <NurseDashboard user={user} />;
      case "acs":
        return <ACSDashboard user={user} />;
      case "especialista":
        return <SpecialistDashboard user={user} />;
      default:
        return <NurseDashboard user={user} />;
    }
  };

  return (
    <Layout>
      <div data-testid="dashboard-page">
        {renderDashboard()}
      </div>
    </Layout>
  );
}
